<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, ref, watch, type PropType } from 'vue';
import { BTTSession, type BTTDataSet } from '~/components/reports/BTTSession';
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue';
const { hasIntermediateAccess } = useAuth();

interface GraphDataPoint {
  timestamp: string | Date
  pressSnsrExtractdBar: number
  pressSnsrrRetractdBar: number
  pullForceKg: number
  tonsSetPoint: number
}

interface BoltGraphItem {
  pegNumber: number
  vault: number
  section: number
  description: string
  startTime: string | Date
  status: string
  dataPoints: GraphDataPoint[]
}

const props = defineProps({
  boltGraphData: {
    type: Array as PropType<BoltGraphItem[]>,
    required: true,
    default: () => []
  }
})

const selectedIndex = ref(0)
const selectedBolt = computed(() => {
  if (!props.boltGraphData || props.boltGraphData.length === 0) return null
  return props.boltGraphData[selectedIndex.value]
})

const boltStats = computed(() => {
  const d = selectedBolt.value?.dataPoints
  if (!d?.length) return null
  const endTime = d[d.length - 1].timestamp
  const durationMs = new Date(endTime).getTime() - new Date(selectedBolt.value!.startTime).getTime()
  const durationSec = Math.round(durationMs / 1000)
  const peakExtract = Math.max(...d.map(p => p.pressSnsrExtractdBar))
  const peakRetract = Math.max(...d.map(p => p.pressSnsrrRetractdBar))
  const peakForce = Math.max(...d.map(p => p.pullForceKg))
  const setPoint = d[d.length - 1].tonsSetPoint
  return { durationSec, peakExtract, peakRetract, peakForce, setPoint }
})

const formatDate = (d: string | Date) => {
  if (!d) return '-'
  return new Date(d).toLocaleString('no-NO', {
    year: 'numeric', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  })
  }

const getStatusText = (status: string | null) => {
  return status !== null ? status : 'not finalized'
}

const getBoltStatusColor = (status: string | null) => {
  const s = status?.toLowerCase() ?? ''
  if (s.includes('reached') || s.includes('complete')) return 'success'
  if (s.includes('not finalized') || s.includes('waiting')) return 'warning'
  if (s.includes('error') || s.includes('stop') || s.includes('fail')) return 'error'
  return 'info'
}

const chartContainer = ref<HTMLDivElement | null>(null)
let chartSession: BTTSession | null = null

const initChart = () => {
  if (!chartContainer.value || !selectedBolt.value) return
  chartSession = new BTTSession({
    chartTitle: ``,
    container: chartContainer.value
  })
  if (selectedBolt.value.dataPoints?.length) {
    chartSession.add(selectedBolt.value.dataPoints as BTTDataSet[])
  }
}

watch(selectedBolt, async (newVal) => {
  if (chartSession) {
    chartSession.bttChart.chart.dispose()
    chartSession = null
  }
  if (newVal) {
    await nextTick()
    initChart()
  }
})

onBeforeUnmount(() => {
  if (chartSession) {
    chartSession.bttChart.chart.dispose()
    chartSession = null
  }
})
</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-card v-else class="d-flex flex-row" style="height: calc(100vh - 210px);" elevation="2">
    <!-- Sidebar -->
    <div class="d-flex flex-column border-e bg-grey-lighten-5" style="width: 280px;">
      <div class="pa-4 bg-white border-b d-flex justify-space-between align-center">
        <span class="text-subtitle-2 font-weight-bold text-uppercase text-medium-emphasis">Bolts</span>
        <v-chip size="x-small" variant="flat" color="grey-lighten-3" class="font-weight-bold text-medium-emphasis">
          {{ boltGraphData.length }}
        </v-chip>
      </div>

      <v-list class="flex-grow-1 overflow-y-auto pa-0 bg-transparent" density="compact">
        <v-list-item
          v-for="(bolt, index) in boltGraphData"
          :key="index"
          :active="index === selectedIndex"
          @click="selectedIndex = index"
          color="primary"
          class="border-b py-3"
          lines="two">
          <div class="d-flex flex-column">
            <div class="d-flex justify-space-between align-baseline mb-2">
              <div class="d-flex align-baseline">
                <span class="text-caption text-uppercase font-weight-bold text-medium-emphasis mr-2">Peg</span>
                <span class="text-h6 font-weight-black text-high-emphasis font-monospace">{{ bolt.pegNumber }}</span>
              </div>
              <div class="d-flex flex-column align-end">
                <v-chip
                  :color="getBoltStatusColor(bolt.status)"
                  size="x-small"
                  class="font-weight-bold text-uppercase mb-1"
                  variant="flat"
                >
                  {{ getStatusText(bolt.status) }}
                </v-chip>
                <span class="text-caption text-medium-emphasis">{{ formatDate(bolt.startTime) }}</span>
              </div>
            </div>

            <div class="d-flex ga-2">
              <div class="d-flex flex-column align-start border rounded px-2 py-1 bg-white flex-grow-1">
                <span class="text-caption text-uppercase font-weight-bold text-medium-emphasis" style="font-size: 0.65rem;">Vault</span>
                <span class="text-body-2 font-weight-bold font-monospace">{{ bolt.vault }}</span>
              </div>
              <div class="d-flex flex-column align-start border rounded px-2 py-1 bg-white flex-grow-1">
                <span class="text-caption text-uppercase font-weight-bold text-medium-emphasis" style="font-size: 0.65rem;">Section</span>
                <span class="text-body-2 font-weight-bold font-monospace">{{ typeof bolt.section === 'number' ? bolt.section.toFixed(1) : bolt.section }}</span>
              </div>
            </div>
          </div>
        </v-list-item>
      </v-list>
    </div>

    <!-- Main Content -->
    <div class="flex-grow-1 d-flex flex-column" style="min-width: 0;">
      <template v-if="selectedBolt">
        <!-- Compact header -->
        <div class="px-4 py-3 border-b d-flex flex-wrap align-center bg-white" style="gap: 12px;">
          <div class="d-flex align-center" style="gap: 8px;">
            <span class="text-subtitle-1 font-weight-bold text-blue-grey-darken-3">
              Peg {{ selectedBolt.description }}
            </span>
            <v-chip :color="getBoltStatusColor(selectedBolt.status)" size="x-small" variant="tonal" class="font-weight-bold text-uppercase">
              {{ selectedBolt.status }}
            </v-chip>
          </div>
          <v-divider vertical class="mx-1" style="height:24px;align-self:center;" />
          <div class="d-flex flex-wrap text-caption text-medium-emphasis align-center" style="gap: 12px;">
            <span><strong>Vault:</strong> {{ selectedBolt.vault }}</span>
            <span><strong>Section:</strong> {{ selectedBolt.section }}</span>
            <span><strong>Start:</strong> {{ formatDate(selectedBolt.startTime) }}</span>
            <span v-if="boltStats"><strong>Duration:</strong> {{ boltStats.durationSec }} s</span>
            <v-divider vertical class="mx-1" style="height:20px;align-self:center;" />
            <span v-if="boltStats"><strong>Peak Extract:</strong> {{ boltStats.peakExtract.toFixed(1) }} bar</span>
            <span v-if="boltStats"><strong>Peak Retract:</strong> {{ boltStats.peakRetract.toFixed(1) }} bar</span>
            <span v-if="boltStats"><strong>Peak Force:</strong> {{ boltStats.peakForce.toFixed(0) }} kg</span>
            <span v-if="boltStats"><strong>Set Point:</strong> {{ boltStats.setPoint.toFixed(2) }} t</span>
          </div>
        </div>

        <!-- Chart -->
        <div ref="chartContainer" class="flex-grow-1 overflow-hidden"></div>
      </template>

      <div v-else class="d-flex flex-column align-center justify-center h-100 bg-grey-lighten-5 rounded border-dashed">
        <div class="text-h2 mb-2">👈</div>
        <div class="text-h6 text-medium-emphasis font-weight-bold">Select a bolt</div>
        <div class="text-body-2 text-medium-emphasis">Choose a bolt from the sidebar to view detailed graph analysis</div>
      </div>
    </div>
  </v-card>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>
